<template>
    <rkm-form ref="RkmForm">
        <rkm-row>
            <rkm-col>
                <rkm-form-input
                    :readonly="!model.tipoId || model.tipoId === enums.declaracaoComparecimentoTipo.PACIENTE"
                    :validations="validations.nome"
                    label="Nome"
                    v-model="model.nome"
                    uppercase
                >
                    <rkm-input-group-append slot="append">
                        <rkm-input-group-text>
                            <rkm-form-radio-group v-model="model.tipoId">
                                <rkm-form-radio :value="enums.declaracaoComparecimentoTipo.PACIENTE">Paciente</rkm-form-radio>
                                <rkm-form-radio :value="enums.declaracaoComparecimentoTipo.ACOMPANHANTE">Acompanhante</rkm-form-radio>
                            </rkm-form-radio-group>
                        </rkm-input-group-text>
                    </rkm-input-group-append>
                </rkm-form-input>
            </rkm-col>

            <rkm-col>
                <rkm-form-input
                    :readonly="!model.documentoTipoId || isDocumentoNumeroBloqueado"
                    :validations="validations.documentoNumero"
                    label="Documento"
                    v-mask="documentoNumeroMask"
                    v-model="model.documentoNumero"
                    uppercase
                >
                    <rkm-input-group-append slot="append">
                        <rkm-input-group-text>
                            <rkm-form-radio-group v-model="model.documentoTipoId">
                                <rkm-form-radio :value="enums.documentoTipo.CPF">CPF</rkm-form-radio>
                                <rkm-form-radio :value="enums.documentoTipo.RG">RG</rkm-form-radio>
                                <rkm-form-radio :value="enums.documentoTipo.CNH">CNH</rkm-form-radio>
                                <rkm-form-radio :value="enums.documentoTipo.CNS">CNS</rkm-form-radio>
                            </rkm-form-radio-group>
                        </rkm-input-group-text>
                    </rkm-input-group-append>
                </rkm-form-input>
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <rkm-form-date-picker
                    label="Data inicial"
                    right
                    v-model="model.dataDe"
                    disabled
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-time-picker
                    label="Hora inicial"
                    v-model="model.horaDe"
                    disabled
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-date-picker
                    :min-date="model.dataDe"
                    :max-date="data.form.finalizado ? data.form.dataDesfecho : data.form.dataAtual"
                    :validations="validations.dataAte"
                    label="Data final"
                    right
                    v-model="model.dataAte"
                />
            </rkm-col>

            <rkm-col>
                <rkm-form-time-picker
                    :validations="validations.horaAte"
                    label="Hora final"
                    v-model="model.horaAte"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <rkm-form-input
                    :validations="validations.motivo"
                    label="Motivo"
                    v-model="model.motivo"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row v-if="onlyForm">
            <rkm-col>
                <rkm-form-select
                    :options="medicos"
                    :validations="validations.medico"
                    :label="impressao ? 'Profissional' : 'Médico'"
                    show-default-option
                    text-field="nome"
                    v-model="model.medicoId"
                    value-field="id"
                />
            </rkm-col>
        </rkm-row>
    </rkm-form>
</template>

<script>
// Enums.
import declaracaoComparecimentoTipo from '@/enums/pronto-atendimento/atendimento-medico/declaracaoComparecimentoTipo'
import documentoTipo from '@/enums/cadastro/documentoTipo'

// Mixins.
import { formats, mask, unmask, repository, error, stringCase } from '@/mixins'

import moment from 'moment'
import { mapActions } from 'vuex'

export default {
    computed: {
        documentoNumeroMask() {
            if (this.model.documentoTipoId === this.enums.documentoTipo.CPF) {
                return this.mask.cpf
            }
            else if (this.model.documentoTipoId === this.enums.documentoTipo.CNS) {
                return this.mask.cns
            }
            else if (this.model.documentoTipoId === this.enums.documentoTipo.RG) {
                return 'NNNNNNNNNNNNNNNNNNNN'
            }
            else {
                return '####################'
            }
        },
        isDocumentoNumeroBloqueado() {
            if (this.model.tipoId !== this.enums.declaracaoComparecimentoTipo.PACIENTE) {
                return false
            }

            if (this.model.documentoTipoId ===  this.enums.documentoTipo.CPF) {
                return !!this.data.form.paciente.cpf
            }
            else if (this.model.documentoTipoId === this.enums.documentoTipo.CNS) {
                return !!this.data.form.paciente.cns
            }

            return false
        },
        getDocumentoNumeroValidation() {
            const validation = {
                required: true,
                min: 2,
                max: 20,
            };

            if (this.model.documentoTipoId ===  this.enums.documentoTipo.CPF) {
                validation.cpf = true
            }
            else if (this.model.documentoTipoId === this.enums.documentoTipo.CNS) {
                validation.cns = true
            }

            return validation
        },
        validations() {
            return {
                nome: {
                    required: true,
                    min: 6,
                    max: 150,
                    termo_invalido: this.model.tipoId === this.enums.declaracaoComparecimentoTipo.ACOMPANHANTE
                        ? this.data.additionals.termosInvalidos : [],
                    name: this.model.tipoId === this.enums.declaracaoComparecimentoTipo.ACOMPANHANTE,
                },
                documentoNumero: this.getDocumentoNumeroValidation,
                dataAte: { required: true },
                horaAte: {
                    required: true,
                    max_hour: this.data.form.finalizado ? this.data.form.horaDesfecho : false
                },
                motivo: {
                    required: true,
                    min: 2,
                    max: 50,
                },
                medico: {
                    required: true,
                }
            }
        },
    },
    created() {
        this.repositories = {
            atendimentoMedico: this.getConectaSusRepository('prontoAtendimento.atendimentoMedico')
        }

        if (this.onlyForm) {

            if (this.impressao) {

                this.setLoading()

                Promise.all([
                    this.repositories.atendimentoMedico.getProfissionaisAtendimento(this.data.atendimentoId),
                    this.repositories.atendimentoMedico.getDeclaracoesComparecimento(this.data.atendimentoId)
                ])
                    .then(([reponseProfissionais, responseDeclaracoes]) => {

                        responseDeclaracoes.data.map(declaracao => {

                            declaracao.dataDe = moment(declaracao.dataDe, 'DD/MM/YYYY').toDate()
                            declaracao.dataAte = moment(declaracao.dataAte, 'DD/MM/YYYY').toDate()
                        })

                        this.medicos = reponseProfissionais.data
                        this.data.form.declaracoesComparecimento = responseDeclaracoes.data
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())

            } else {

                this.setLoading()

                Promise.all([
                    this.repositories.atendimentoMedico.getMedicosAtendimento(this.data.atendimentoId),
                    this.repositories.atendimentoMedico.getDeclaracoesComparecimento(this.data.atendimentoId)
                ])
                    .then(([reponseMedicos, responseDeclaracoes]) => {

                        responseDeclaracoes.data.map(declaracao => {

                            declaracao.dataDe = moment(declaracao.dataDe, 'DD/MM/YYYY').toDate()
                            declaracao.dataAte = moment(declaracao.dataAte, 'DD/MM/YYYY').toDate()
                        })

                        this.medicos = reponseMedicos.data
                        this.data.form.declaracoesComparecimento = responseDeclaracoes.data
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        }

        this.load()
    },
    data: () => ({
        moment,
        enums: { declaracaoComparecimentoTipo, documentoTipo },
        medicos: []
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.model.dataDe ??= this.data.form.recepcao.dataDe
            this.model.horaDe ??= this.data.form.recepcao.horaDe
        },
        save() {
            this.$refs.RkmForm.validate().then(response => {
                if (response === true) {
                    const de = moment(moment(this.model.dataDe).format('YYYY-MM-DD') + ' ' + this.model.horaDe).format('YYYY-MM-DD HH:mm')
                    const ate = moment(moment(this.model.dataAte).format('YYYY-MM-DD') + ' ' + this.model.horaAte).format('YYYY-MM-DD HH:mm')

                    if(de >= ate) {
                        this.createNotification({
                            type: 'alert',
                            color: 'danger',
                            title: 'Ação não permitida',
                            message: 'A data e hora inicial deve ser anterior a data e hora final'
                        })

                        return false
                    }

                    if (this.possuiHorarioConflitante()) {
                        this.createNotification({
                            type: 'alert',
                            color: 'danger',
                            title: 'Ação não permitida',
                            message: 'Já existe uma declaração na mesma data e intervalo de hora'
                        })

                        return false
                    }

                    if (this.onlyForm) {

                        this.setLoading()

                        this.repositories.atendimentoMedico.storeDeclaracaoComparecimento(this.prepareData())
                            .then(() => {

                                this.showSaveNotification()
                                this.$emit('saved', this.model)
                            })
                            .catch(this.handleError)
                            .finally(() => this.unsetLoading())
                    }
                    else {

                        if (this.model.id) {
                            const index = this.data.model.declaracoesComparecimento.findIndex(item => item.id === this.model.id)
                            Object.assign(this.data.model.declaracoesComparecimento[index], this.model)
                        } else {
                            this.data.model.declaracoesComparecimento.push({
                                id: this.uniqueId('pronto-atendimento-declaracao-comparecimento'),
                                lotacao: {
                                    ocupacao: {
                                        codigo: this.data.form.lotacao.ocupacao.codigo,
                                    },
                                    profissional: {
                                        nome: this.data.form.lotacao.profissional.nome,
                                    },
                                },
                                tipoId: this.model.tipoId,
                                nome: this.model.nome,
                                documentoNumero: this.model.documentoNumero,
                                documentoTipoId: this.model.documentoTipoId,
                                dataDe: this.model.dataDe,
                                horaDe: this.model.horaDe,
                                dataAte: this.model.dataAte,
                                horaAte: this.model.horaAte,
                                motivo: this.model.motivo,
                            })
                        }

                        this.$emit('saved', this.model)
                    }
                }
            })
        },
        prepareData() {

            const de = moment(moment(this.model.dataDe).format('YYYY-MM-DD') + ' ' + this.model.horaDe).format('YYYY-MM-DD HH:mm')
            const ate = moment(moment(this.model.dataAte).format('YYYY-MM-DD') + ' ' + this.model.horaAte).format('YYYY-MM-DD HH:mm')
            let documentoNumero = this.model.documentoNumero

            switch (this.model.documentoTipoId) {
                case this.enums.documentoTipo.CPF:
                    documentoNumero = this.unMaskCpf(documentoNumero)
                    break;

                case this.enums.documentoTipo.CNS:
                    documentoNumero = this.unMaskCns(documentoNumero)
                    break;
            }

            return  this.camelObjectKeysToSnakeRecursive({
                atendimentoId: this.data.atendimentoId,
                tipoId: this.model.tipoId,
                nome: this.model.nome,
                documentoTipoId: this.model.documentoTipoId,
                documentoNumero: documentoNumero,
                de: de,
                ate: ate,
                motivo: this.model.motivo,
                medicoId: this.model.medicoId,
            })
        },
        possuiHorarioConflitante() {

            const de = moment(moment(this.model.dataDe).format('YYYY-MM-DD') + ' ' + this.model.horaDe)
            const ate = moment(moment(this.model.dataAte).format('YYYY-MM-DD') + ' ' + this.model.horaAte)

            return !!this.data.form.declaracoesComparecimento.concat(this.data.model.declaracoesComparecimento)
                .filter(d => d.id !== this.model.id && d.tipoId === this.model.tipoId)
                .find(declaracao => {

                    const deAtual = moment(moment(declaracao.dataDe).format('YYYY-MM-DD') + ' ' + declaracao.horaDe)
                    const ateAtual = moment(moment(declaracao.dataAte).format('YYYY-MM-DD') + ' ' + declaracao.horaAte)

                    return (de >= deAtual && de <= ateAtual)
                        || (ate >= deAtual && ate <= ateAtual)
                        || (de <= deAtual && ate >= ateAtual)
                })
        },
        setDocumentoTipoId(value) {
            this.$nextTick(() => {
                if (this.model.tipoId === this.enums.declaracaoComparecimentoTipo.PACIENTE) {
                    if (value ===  this.enums.documentoTipo.CPF) {
                        this.model.documentoNumero = this.formatCpf(this.data.form.paciente.cpf || '')
                    }
                    else if (value === this.enums.documentoTipo.RG) {
                        this.model.documentoNumero = this.data.form.paciente.rg
                    }
                    else if (value === this.enums.documentoTipo.CNH) {
                        this.model.documentoNumero = this.data.form.paciente.cnh
                    }
                    else if (value === this.enums.documentoTipo.CNS) {
                        this.model.documentoNumero = this.formatCns(this.data.form.paciente.cns || '')
                    }
                }
                else {
                    this.model.documentoNumero = ''
                }
            })
        },
    },
    mixins: [formats, mask, unmask, repository, error, stringCase],
    props: {
        data: { type: Object, required: true },
        model: { type: Object, required: true },
        onlyForm: { type: Boolean, default: false },
        impressao: { type: Boolean, default: false },
    },
    watch: {
        'model.tipoId'(value) {
            if (value === this.enums.declaracaoComparecimentoTipo.PACIENTE) {
                this.model.nome = this.data.form.paciente.nome
            } else {
                this.model.nome = this.data.form.recepcao.acompanhante
            }

            this.setDocumentoTipoId(this.model.documentoTipoId)
        },
        'model.documentoTipoId'(value) {
            this.setDocumentoTipoId(value)
        }
    },
}
</script>